import React, { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { theme } from '../themes';
import { addBasemap } from '../utils/basemap';

// --- DEMO DATA: Talladega easter eggs (only render if theme is the talladega skin) ---
const TALLADEGA_DEMO_VENUES = [
  { id: 'girls1', name: 'Bender Girls (Eastaboga Blvd)', lat: 33.56545, lng: -86.06290, type: 'supply', deal: 'Free Bender Extender!' },
  { id: 'girls2', name: 'Bender Girls (Talladega Blvd)', lat: 33.56305, lng: -86.06410, type: 'supply', deal: 'Free Bender Extender!' },
  { id: 'pill2',  name: 'Bender Extender Main Tent',     lat: 33.56385, lng: -86.06780, type: 'supply',  deal: 'Free Samples' },
  { id: 'food1',  name: 'Fan Zone Concessions',          lat: 33.56355, lng: -86.06850, type: 'vendor',  deal: 'Hotdogs & Merch' },
  { id: 'bath1',  name: 'Garage Restrooms',              lat: 33.56450, lng: -86.06920, type: 'bathroom', deal: null },
  { id: 'pill1',  name: 'UAB Medicine First Aid',        lat: 33.56425, lng: -86.06995, type: 'supply',   deal: 'Water & Medical' },
  { id: 'beer4',  name: 'Garage Experience Bar',         lat: 33.56500, lng: -86.06860, type: 'drink',    deal: 'Draft Beer' },
  { id: 'beer1',  name: 'Boulevard Party Zone',          lat: 33.56280, lng: -86.06550, type: 'drink',    deal: 'Ice Cold Beer' },
  { id: 'beer2',  name: 'The Big One Bar (5 Points)',    lat: 33.56275, lng: -86.06360, type: 'drink',    deal: 'Signature Cocktails' },
  { id: 'beer3',  name: 'Superstretch Bar',              lat: 33.56500, lng: -86.06080, type: 'drink',    deal: 'Margaritas' },
];

// Only seed demo venues if the active theme is the talladega skin.
const SAMPLE_VENUES = theme.id === 'talladega' ? TALLADEGA_DEMO_VENUES : [];

// Minimal HTML escape for any user-supplied text injected into divIcon markup.
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (m) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]
));

const MapEngine = ({ userLocation, path, squadMembers, broadcasters, stats, recenterTrigger, db, appId }) => {
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const squadMarkersRef = useRef({});
  const venueMarkersRef = useRef({});
  const broadcasterMarkersRef = useRef({});
  const pathLineRef = useRef(null);

  const [venues, setVenues] = useState([]);
  const hasCenteredRef = useRef(false);

  // --- 1. INITIALIZE MAP (tile URL now comes from active theme) ---
  useEffect(() => {
    const container = document.getElementById('map-container');
    if (!container || !window.L) return;
    if (mapRef.current) { mapRef.current.invalidateSize(); return; }

    const L = window.L;

    try {
      const initialCenter = userLocation || (theme.id === 'bender' ? [20, 0] : [39.8283, -98.5795]);
      const initialZoom   = userLocation ? 16 : (theme.id === 'bender' ? 2 : 4);

      const map = L.map('map-container', {
        zoomControl: false,
        attributionControl: false,
        fadeAnimation: true,
        zoomAnimation: true,
        markerZoomAnimation: true,
      }).setView(initialCenter, initialZoom);

      // --- TILE LAYER FROM THEME ---
      // Vector (MapLibre) when the theme has a styleUrl, raster tiles otherwise.
      addBasemap(map, theme.map);

      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 200);
    } catch (err) {
      console.error('Map Init Failed:', err);
    }

    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // --- 2. FIRST-LOAD CAMERA ---
  useEffect(() => {
    if (!mapRef.current || !userLocation) return;
    if (!hasCenteredRef.current) {
      mapRef.current.flyTo(userLocation, 16, { duration: 2.5 });
      hasCenteredRef.current = true;
    }
  }, [userLocation]);

  // --- 3. RECENTER BUTTON ---
  useEffect(() => {
    if (!mapRef.current || !userLocation || recenterTrigger === 0) return;
    mapRef.current.flyTo(userLocation, 16, { duration: 1.5, easeLinearity: 0.25 });
  }, [recenterTrigger, userLocation]);

  // --- 4. FETCH VENUES ---
  useEffect(() => {
    if (!db || !appId) {
      setVenues(SAMPLE_VENUES);
      return;
    }
    const venuesRef = collection(db, 'artifacts', appId, 'public', 'data', 'venues');
    const unsub = onSnapshot(venuesRef, (snap) => {
      const vList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVenues([...SAMPLE_VENUES, ...vList]);
    });
    return () => unsub();
  }, [db, appId]);

  // --- 5. RENDER VENUES (emoji/colors now come from theme.venueTypes) ---
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    const L = window.L;
    const map = mapRef.current;

    venues.forEach(v => {
      if (!venueMarkersRef.current[v.id]) {
        // Look up venue type config from active theme, fall back to default
        const typeCfg = theme.venueTypes[v.type] || theme.venueTypes.default;
        const { emoji, bg, border } = typeCfg;

        const vIcon = L.divIcon({
          className: 'bg-transparent border-none',
          html: `<div class="w-8 h-8 rounded-full border-2 ${border} ${bg} flex items-center justify-center text-lg shadow-[0_0_10px_rgba(0,0,0,0.8)] backdrop-blur-sm transition-transform hover:scale-110">${emoji}</div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        const marker = L.marker([v.lat, v.lng], { icon: vIcon }).addTo(map);

        const popupContent = `
          <div class="text-center font-sans p-1">
            <strong class="text-white block text-xs font-black uppercase tracking-tight">${v.name}</strong>
            ${v.deal ? `<span class="text-[10px] font-bold uppercase mt-1 block tracking-wider" style="color:${theme.raw.accentPrimary}">${v.deal}</span>` : ''}
          </div>
        `;
        marker.bindPopup(popupContent, { closeButton: false, offset: [0, -10] });

        venueMarkersRef.current[v.id] = marker;
      } else {
        venueMarkersRef.current[v.id].setLatLng([v.lat, v.lng]);
      }
    });

    const currentVenueIds = venues.map(v => v.id);
    Object.keys(venueMarkersRef.current).forEach(id => {
      if (!currentVenueIds.includes(id)) {
        mapRef.current.removeLayer(venueMarkersRef.current[id]);
        delete venueMarkersRef.current[id];
      }
    });
  }, [venues]);

  // --- 6. GPS VISUALS (me marker, path, squad — all theme-driven colors) ---
  useEffect(() => {
    if (!mapRef.current || !window.L || !userLocation) return;
    const L = window.L;

    if (pathLineRef.current) {
      pathLineRef.current.setLatLngs(path);
    } else {
      pathLineRef.current = L.polyline(path, {
        color:    theme.raw.accentSecondary,
        weight:   5,
        opacity:  0.8,
        lineCap:  'round',
        lineJoin: 'round',
      }).addTo(mapRef.current);
    }

    if (markersRef.current.me) {
      markersRef.current.me.setLatLng(userLocation);
    } else {
      const myIcon = L.divIcon({
        className: 'bg-transparent border-none',
        html: `<div class="w-4 h-4 rounded-full border-2 border-white animate-pulse" style="background:${theme.raw.accentPrimary};box-shadow:0 0 20px ${theme.raw.pinGlow}"></div>`,
        iconSize: [16, 16],
      });
      markersRef.current.me = L.marker(userLocation, { icon: myIcon, zIndexOffset: 1000 }).addTo(mapRef.current);
    }

    squadMembers.forEach(member => {
      if (member.location) {
        if (squadMarkersRef.current[member.id]) {
          squadMarkersRef.current[member.id].setLatLng(member.location);
        } else {
          const friendIcon = L.divIcon({
            className: 'bg-transparent border-none',
            html: `<div class="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-black text-black shadow-xl scale-90 transition-transform hover:scale-110" style="background:${theme.raw.squadPin}">${member.name ? member.name.slice(0,2).toUpperCase() : '??'}</div>`,
            iconSize: [32, 32],
          });
          squadMarkersRef.current[member.id] = L.marker(member.location, { icon: friendIcon }).addTo(mapRef.current);
        }
      }
    });
  }, [userLocation, path, squadMembers]);

  // --- 7. RENDER BROADCASTERS (reps sharing live location to all users) ---
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    const L = window.L;
    const map = mapRef.current;
    const list = broadcasters || [];

    list.forEach((b) => {
      if (!Array.isArray(b.location)) return;
      const name = esc(b.displayName || 'Bender Rep');
      const existing = broadcasterMarkersRef.current[b.id];

      if (existing) {
        existing.setLatLng(b.location);
      } else {
        const icon = L.divIcon({
          className: 'bg-transparent border-none',
          html: `
            <div style="display:flex;flex-direction:column;align-items:center">
              <div class="animate-pulse" style="width:34px;height:34px;border-radius:9999px;border:3px solid #fff;background:${theme.raw.accentPrimary};display:flex;align-items:center;justify-content:center;font-size:17px;box-shadow:0 0 18px ${theme.raw.pinGlow},0 0 32px ${theme.raw.pinGlow}">🔥</div>
              <div style="margin-top:3px;background:${theme.raw.accentPrimary};color:#000;font-weight:900;font-size:9px;text-transform:uppercase;letter-spacing:0.05em;padding:2px 6px;border-radius:9999px;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.6)">${name}</div>
            </div>`,
          iconSize: [34, 50],
          iconAnchor: [17, 17],
        });
        const marker = L.marker(b.location, { icon, zIndexOffset: 2000 }).addTo(map);
        marker.bindPopup(
          `<div class="text-center font-sans p-1">
             <strong class="text-white block text-xs font-black uppercase tracking-tight">${name}</strong>
             <span class="text-[10px] font-bold uppercase mt-1 block tracking-wider" style="color:${theme.raw.accentPrimary}">Find me — Bender Vender</span>
           </div>`,
          { closeButton: false, offset: [0, -12] }
        );
        broadcasterMarkersRef.current[b.id] = marker;
      }
    });

    // Remove markers for reps who went off-air / stale.
    const liveIds = list.map((b) => b.id);
    Object.keys(broadcasterMarkersRef.current).forEach((id) => {
      if (!liveIds.includes(id)) {
        map.removeLayer(broadcasterMarkersRef.current[id]);
        delete broadcasterMarkersRef.current[id];
      }
    });
  }, [broadcasters]);

  return (
    <div
      id="map-container"
      className="flex-grow w-full h-full relative z-0"
      style={{ background: theme.map.containerBg }}
    />
  );
};

export default MapEngine;
